import React, { useRef, useMemo, useEffect, useState } from "react";
import styled from "@emotion/styled";
import {
  Add as AddIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  Cached as CachedIcon,
  Delete as DeleteIcon,
} from "@material-ui/icons";
import { CameraOff as CameraOffIcon } from "react-feather";
import { Box, Divider, IconButton, Typography } from "@material-ui/core";
import theme from "src/theme";

const AddButton = styled.label`
  margin-left: auto;
  cursor: pointer;
  color: ${theme.palette.info.main};
  padding: 12px;
  border-radius: 50%;

  &:hover {
    background: rgba(0, 0, 0, 0.04);
  }

  transition: background-color 150ms cubic-bezier(0.4, 0, 0.2, 1) 0ms;
`;

const InputFile = styled.input`
  display: none;
`;

const Header = styled.div`
  margin: 0 auto;
  width: 90%;

  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const HeaderButtons = styled.div`
  display: flex;
  align-items: center;

  padding: 0 8px;
`;

const Image = styled.img`
  margin-top: 8px;
  width: 100%;
  height: 100%;
`;

const Preview = styled.img`
  filter: ${(props) => (props.active ? "none" : "grayscale(100%)")};

  border-radius: 8px;
  cursor: pointer;

  width: 60px;
  height: 50px;
`;

const PreviewContainer = styled.div`
  position: relative;

  display: flex;
  gap: 2px;
  margin-top: 4px;
  width: 100%;
  overflow: auto;

  &::-webkit-scrollbar {
    display: none;
  }
`;

function MultipleImageUploader({
  templateUrl = "",
  data = [],
  identifier,
  width = "auto",
  setImagesToUpload = () => {},
  imagesToUpload = [],
}) {
  const [images, setImages] = useState([]);
  const [update, setUpdate] = useState(false);
  const [selectedImage, setSelectedImage] = useState({
    image: "",
    id: 0,
    upload: 0,
    update: 0,
    original: "",
  });

  const inputRef = useRef(null);
  const slideRef = useRef(null);

  useEffect(() => {
    if (!data.length) return;
    let imagens = [];

    data.map(identifier).map((item, index) => {
      if (!index) {
        setSelectedImage({ image: templateUrl.replace("#", item), id: item });
      }

      imagens.push({ image: templateUrl.replace("#", item), id: item });

      if (index === data.length - 1) {
        setImages(imagens);
      }

      return 0;
    });
  }, [data]);

  const handleChange = async () => {
    const inputImage = inputRef.current.files[0];
    if (inputImage) {
      const fileReader = new FileReader();

      fileReader.readAsDataURL(inputImage);
      fileReader.onload = () => {
        if (fileReader.readyState === 2) {
          if (update) {
            setSelectedImage({
              image: fileReader.result,
              id: selectedImage.id,
              upload: 1,
              update: 1,
              original: selectedImage.update
                ? selectedImage.original
                : selectedImage.image,
            });

            if (selectedImage.update) {
              setImagesToUpload([
                ...imagesToUpload.filter(
                  (item) => item.id !== selectedImage.id
                ),
                {
                  image: fileReader.result,
                  id: selectedImage.id,
                  upload: 1,
                  update: 1,
                  original: selectedImage.original,
                },
              ]);
            } else {
              setImages(images.filter((item) => item.id !== selectedImage.id));

              setImagesToUpload([
                ...imagesToUpload,
                {
                  image: fileReader.result,
                  id: selectedImage.id,
                  upload: 1,
                  update: 1,
                  original: fileReader.result,
                },
              ]);
            }

            setUpdate(false);
          } else {
            setSelectedImage({
              image: fileReader.result,
              id: `tmp${imagesToUpload.length}`,
              upload: 1,
              update: 0,
              original: fileReader.result,
            });

            setImagesToUpload([
              ...imagesToUpload,
              {
                image: fileReader.result,
                id: `tmp${imagesToUpload.length}`,
                upload: 1,
                update: 0,
                original: fileReader.result,
              },
            ]);
          }
        }
      };
    }
  };

  const handleSelectImage = (image) => setSelectedImage(image);

  const handleScroll = (direction) => {
    if (direction === "left") {
      slideRef.current.scrollLeft -= 60;
    } else {
      slideRef.current.scrollLeft += 60;
    }
  };

  const handleUpdateImage = () => {
    setUpdate(true);
    inputRef.current.click();
  };

  const handleDeleteImage = () => {
    if (selectedImage.upload === 1) {
      if (selectedImage.update) {
        setImagesToUpload(
          imagesToUpload.filter((item) => item.id !== selectedImage.id)
        );

        setImages([
          ...images,
          {
            image: selectedImage.original,
            id: selectedImage.id,
          },
        ]);

        setSelectedImage({
          image: selectedImage.original,
          id: selectedImage.id,
          original: selectedImage.original,
        });
      } else {
        setImagesToUpload(
          imagesToUpload.filter((item) => item.id !== selectedImage.id)
        );

        setSelectedImage({});
      }
    } else {
      setImages(images.filter((item) => item.id !== selectedImage.id));
      setImagesToUpload([
        ...imagesToUpload,
        { id: selectedImage.id, delete: 1 },
      ]);

      setSelectedImage({});
    }
  };

  return (
    <Box display="flex" flexDirection="column" gap="4px" width={width}>
      <Header>
        Ações
        <HeaderButtons>
          <AddButton>
            <InputFile onChange={handleChange} ref={inputRef} type="file" />
            <AddIcon />
          </AddButton>
          {selectedImage.upload !== 1 || selectedImage.update === 1 ? (
            <>
              <IconButton onClick={handleUpdateImage}>
                <CachedIcon htmlColor={theme.palette.warning.main} />
              </IconButton>
            </>
          ) : null}
          <IconButton onClick={handleDeleteImage}>
            <DeleteIcon htmlColor={theme.palette.error.main} />
          </IconButton>
        </HeaderButtons>
      </Header>
      <Divider />
      {selectedImage.id ? (
        <Image src={selectedImage.image} alt="" />
      ) : (
        <Box
          width="100%"
          height="300px"
          display="flex"
          flexDirection="column"
          justifyContent="center"
          alignItems="center"
        >
          <CameraOffIcon />
          <Typography align="center"> Nenhuma imagem encontrada </Typography>
        </Box>
      )}
      <Box display="flex" alignItems="center">
        {(imagesToUpload.length > 3 ||
          images.length > 3 ||
          imagesToUpload.length + images.length > 3) && (
          <IconButton onClick={() => handleScroll("left")}>
            <ChevronLeftIcon />
          </IconButton>
        )}
        <PreviewContainer ref={slideRef}>
          {images.map((item) => (
            <Preview
              active={item.id === selectedImage.id ? true : false}
              onClick={() => handleSelectImage(item)}
              src={item.image}
              key={item.id}
              alt=""
            />
          ))}
          {imagesToUpload
            .filter((item) => !item.delete)
            .map((item, index) => (
              <Preview
                active={item.id === selectedImage.id ? true : false}
                onClick={() => handleSelectImage(item)}
                src={item.image}
                key={`upload-${index}`}
                alt=""
              />
            ))}
        </PreviewContainer>
        {(imagesToUpload.length > 3 ||
          images.length > 3 ||
          imagesToUpload.length + images.length > 3) && (
          <IconButton onClick={() => handleScroll("right")}>
            <ChevronRightIcon />
          </IconButton>
        )}
      </Box>
    </Box>
  );
}

export default MultipleImageUploader;
